import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine, faEye, faHeart, faTimesCircle, faLink,
  faUnlock, faBrain, faStar, faRefresh, faSpinner,
  faUsers, faClock, faFilter, faArrowTrendDown,
  faGhost, faCheckCircle, faHourglassEnd, faArrowUp, faArrowDown,
} from "@fortawesome/free-solid-svg-icons";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

const EVENT_META = {
  view_start: { label: "Profile Views", icon: faEye, color: "#6366F1" },
  view_end: { label: "View Sessions (completed)", icon: faEye, color: "#818CF8" },
  photo_swipe: { label: "Photo Swipes", icon: faHeart, color: "#EC4899" },
  photo_zoom: { label: "Photo Zooms", icon: faEye, color: "#F472B6" },
  alignment_tab_open: { label: "DNA Tab Opens", icon: faBrain, color: "#7C3AED" },
  astrology_tab_open: { label: "Astrology Tab Opens", icon: faStar, color: "#C9A84C" },
  hobbies_read: { label: "Hobbies Read", icon: faEye, color: "#10B981" },
  profile_save: { label: "Profiles Saved", icon: faHeart, color: "#EF4444" },
  pass: { label: "Passes (Skipped)", icon: faTimesCircle, color: "#94A3B8" },
  connection_sent: { label: "Connects Sent", icon: faLink, color: "#059669" },
  score_unlock: { label: "Score Unlocks", icon: faUnlock, color: "#F59E0B" },
  alignment_unlock: { label: "Alignment Unlocks", icon: faUnlock, color: "#D97706" },
  score_view: { label: "Score Views", icon: faEye, color: "#60A5FA" },
  return_visit: { label: "Return Visits", icon: faChartLine, color: "#6366F1" },
  report: { label: "Reports Filed", icon: faTimesCircle, color: "#EF4444" },
  block: { label: "Blocks", icon: faTimesCircle, color: "#DC2626" },
};

const REJECTION_LABELS = {
  location: "Location",
  age_gap: "Age Gap",
  career_lifestyle: "Career/Lifestyle",
  community_preference: "Community",
  photos: "Photos",
  alignment_score_low: "Low Match Score",
  just_browsing: "Just Browsing",
  other: "Other",
};

const BAR_COLORS = ["#6366F1", "#7C3AED", "#EC4899", "#F59E0B", "#10B981", "#60A5FA", "#EF4444", "#94A3B8"];

const MiniBar = ({ label, count, max, colorClass, color }) => {
  const pct = max > 0 ? Math.max((count / max) * 100, count > 0 ? 2 : 0) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-xs text-slate-500 w-40 truncate shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-bold text-slate-700 w-14 text-right shrink-0">{fmt(count)}</span>
    </div>
  );
};

const DailyChart = ({ data }) => {
  if (!data?.length) return <div className="flex items-center justify-center h-20 text-xs text-slate-300">No data</div>;
  const vals = data.map(d => d.dau ?? 0);
  const max = Math.max(...vals, 1);
  const W = 400, H = 80;
  const pts = vals.map((v, i) => {
    const x = (i / Math.max(vals.length - 1, 1)) * W;
    const y = H - 4 - ((v / max) * (H - 16));
    return `${x},${y}`;
  }).join(" ");
  const area = `0,${H} ` + pts + ` ${W},${H}`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none">
        <defs>
          <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366F1" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#dauFill)" />
        <polyline points={pts} fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{data[0]?._id || data[0]?.date}</span>
        <span>{data[data.length - 1]?._id || data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const DailyConnectsChart = ({ data }) => {
  if (!data?.length) return <div className="flex items-center justify-center h-20 text-xs text-slate-300">No data</div>;
  const maxSent = Math.max(...data.map(d => d.sent ?? 0), 1);
  const W = 400, H = 80;
  const sentPts = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * W;
    const y = H - 4 - (((d.sent ?? 0) / maxSent) * (H - 16));
    return `${x},${y}`;
  }).join(" ");
  const acceptedPts = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * W;
    const y = H - 4 - (((d.accepted ?? 0) / maxSent) * (H - 16));
    return `${x},${y}`;
  }).join(" ");
  return (
    <div>
      <div className="flex items-center gap-4 mb-2">
        <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-1 rounded bg-indigo-400 inline-block" />Sent</span>
        <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-1 rounded bg-emerald-400 inline-block" />Accepted</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none">
        <polyline points={sentPts} fill="none" stroke="#818CF8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={acceptedPts} fill="none" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 2" />
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{data[0]?._id}</span>
        <span>{data[data.length - 1]?._id}</span>
      </div>
    </div>
  );
};

const EngagementAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectsData, setConnectsData] = useState(null);
  const [connectsLoading, setConnectsLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadConnects = useCallback(async (params = {}) => {
    setConnectsLoading(true);
    try {
      const res = await func.get("mst-admin/analytics/connects", { params });
      if (res?.status === 200 && res.data?.success) {
        setConnectsData(res.data);
      }
    } catch {}
    setConnectsLoading(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const [engRes] = await Promise.all([
        func.get("mst-admin/analytics/engagement", { params }),
        loadConnects(params),
      ]);
      if (engRes?.status === 200 && engRes.data?.success) {
        setData(engRes.data);
      } else {
        toast.error("Failed to load analytics");
      }
    } catch {
      toast.error("Failed to load analytics");
    }
    setLoading(false);
  }, [startDate, endDate, loadConnects]);

  useEffect(() => { load(); }, []);

  const eventBreakdown = data?.eventBreakdown ?? [];
  const passReasons = data?.passReasons ?? [];
  const dailyActiveUsers = data?.dailyActiveUsers ?? [];
  const featureAdoption = data?.featureAdoption ?? [];
  const alignmentCompletion = data?.alignmentCompletion ?? [];
  const viewDuration = data?.viewDuration ?? {};

  const maxEvent = Math.max(...eventBreakdown.map(e => e.count ?? 0), 1);
  const maxPass = Math.max(...passReasons.map(p => p.count ?? 0), 1);

  // Compute feature adoption rate (vs total interacting users)
  const totalUsers = data?.totalInteractingUsers ?? 1;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-violet-700 px-6 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <FontAwesomeIcon icon={faChartLine} className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Engagement Analytics</h1>
                <p className="text-indigo-200 text-xs">Real user behavior from ProfileInteraction events</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-white/15 rounded-xl px-3 py-2 border border-white/20">
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="text-xs text-white bg-transparent outline-none w-28" />
                <span className="text-white/40 text-xs">→</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="text-xs text-white bg-transparent outline-none w-28" />
              </div>
              <button onClick={load}
                className="flex items-center gap-2 px-3 py-2 bg-white/20 text-white text-xs font-semibold rounded-xl hover:bg-white/30 transition border border-white/20">
                <FontAwesomeIcon icon={loading ? faSpinner : faRefresh} spin={loading} />
                Apply
              </button>
            </div>
          </div>

          {/* Top KPIs */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Active Users (in range)", value: fmt(data?.totalInteractingUsers), icon: faUsers },
              { label: "Avg View Duration", value: viewDuration.avg ? `${Math.round(viewDuration.avg)}s` : "—", icon: faClock },
              { label: "Total View Sessions", value: fmt(viewDuration.totalViews), icon: faEye },
            ].map(s => (
              <div key={s.label} className="bg-white/15 rounded-xl px-4 py-3 backdrop-blur-sm border border-white/20">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={s.icon} className="text-indigo-200 text-xs" />
                  <p className="text-indigo-200 text-xs">{s.label}</p>
                </div>
                <p className="text-white text-2xl font-bold">{loading ? "—" : s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-indigo-300" />
          </div>
        ) : (
          <>
            {/* Daily Active Users Chart */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-semibold text-slate-800">Daily Active Users</h2>
                  <p className="text-xs text-slate-400">Unique users with any interaction per day</p>
                </div>
              </div>
              <DailyChart data={dailyActiveUsers} />
            </div>

            {/* Event Breakdown + Pass Reasons */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Event Breakdown */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h2 className="font-semibold text-slate-800 mb-1">Event Breakdown</h2>
                <p className="text-xs text-slate-400 mb-5">All user actions, ranked by volume</p>
                <div className="space-y-0.5">
                  {eventBreakdown.map((e, i) => {
                    const meta = EVENT_META[e._id] ?? { label: e._id, color: "#6366F1" };
                    return (
                      <MiniBar key={e._id}
                        label={meta.label}
                        count={e.count}
                        max={maxEvent}
                        color={meta.color}
                      />
                    );
                  })}
                  {eventBreakdown.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-6">No events in selected range</p>
                  )}
                </div>
              </div>

              {/* Pass Reasons */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faArrowTrendDown} className="text-red-400 text-sm" />
                  <h2 className="font-semibold text-slate-800">Why Users Skip Profiles</h2>
                </div>
                <p className="text-xs text-slate-400 mb-5">Pass/skip rejection reasons — what to improve</p>
                <div className="space-y-0.5">
                  {passReasons.map((r, i) => (
                    <MiniBar key={r._id}
                      label={REJECTION_LABELS[r._id] ?? r._id}
                      count={r.count}
                      max={maxPass}
                      color={BAR_COLORS[i % BAR_COLORS.length]}
                    />
                  ))}
                  {passReasons.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-6">No pass reason data yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Feature Adoption */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h2 className="font-semibold text-slate-800 mb-1">Feature Adoption</h2>
              <p className="text-xs text-slate-400 mb-5">
                % of active users who have used each premium feature
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {featureAdoption.map((f, i) => {
                  const meta = EVENT_META[f.feature] ?? { label: f.feature, color: "#6366F1" };
                  const adoptionPct = totalUsers > 0 ? Math.round((f.uniqueUsers / totalUsers) * 100) : 0;
                  return (
                    <div key={f.feature} className="text-center bg-slate-50 rounded-xl p-4">
                      <div
                        className="relative w-16 h-16 mx-auto mb-3"
                      >
                        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                          <circle cx="18" cy="18" r="14" fill="none" stroke="#F1F5F9" strokeWidth="4" />
                          <circle cx="18" cy="18" r="14" fill="none"
                            stroke={meta.color}
                            strokeWidth="4"
                            strokeDasharray={`${adoptionPct * 0.88} ${88 - adoptionPct * 0.88}`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold text-slate-700">{adoptionPct}%</span>
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-slate-700">{meta.label}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{fmt(f.uniqueUsers)} users</p>
                    </div>
                  );
                })}
                {featureAdoption.length === 0 && (
                  <p className="col-span-5 text-xs text-slate-400 text-center py-6">No feature adoption data yet</p>
                )}
              </div>
            </div>

            {/* Alignment Completion Distribution */}
            {alignmentCompletion.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h2 className="font-semibold text-slate-800 mb-1">Match DNA Completion Distribution</h2>
                <p className="text-xs text-slate-400 mb-5">How many questions users have answered</p>
                <div className="flex items-end gap-3 h-28">
                  {alignmentCompletion.map((bucket, i) => {
                    const maxCount = Math.max(...alignmentCompletion.map(b => b.count ?? 0), 1);
                    const h = ((bucket.count / maxCount) * 100);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-[10px] text-slate-500 font-semibold">{fmt(bucket.count)}</span>
                        <div className="w-full rounded-t-md transition-all" style={{ height: `${h}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} />
                        <span className="text-[10px] text-slate-400">{bucket._id === '100+' ? '100+' : bucket._id === 0 ? '0' : `${bucket._id}+`}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ─── Connects Intelligence ─── */}
        <div className="bg-gradient-to-r from-emerald-700 to-teal-700 rounded-2xl px-5 py-4 flex items-center gap-3 -mx-0">
          <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faLink} className="text-white text-sm" />
          </div>
          <div>
            <h2 className="font-bold text-white text-base">Connects Intelligence</h2>
            <p className="text-emerald-200 text-xs">Match connection patterns and acceptance health</p>
          </div>
        </div>

        {connectsLoading ? (
          <div className="flex justify-center py-10">
            <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-emerald-300" />
          </div>
        ) : connectsData ? (
          <>
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Acceptance Rate", value: connectsData.summary?.acceptanceRate, icon: faCheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
                { label: "Ghost Connects", value: fmt(connectsData.summary?.ghostConnects), icon: faGhost, color: "text-orange-500", bg: "bg-orange-50", sub: connectsData.summary?.ghostRate },
                { label: "Avg Response Time", value: connectsData.summary?.avgResponseHours ? `${connectsData.summary.avgResponseHours}h` : "—", icon: faClock, color: "text-indigo-500", bg: "bg-indigo-50" },
                { label: "Total Connects", value: fmt(connectsData.summary?.total), icon: faLink, color: "text-slate-600", bg: "bg-slate-50" },
              ].map(s => (
                <div key={s.label} className={`rounded-2xl ${s.bg} p-4 border border-white shadow-sm`}>
                  <div className="flex items-center gap-2 mb-1">
                    <FontAwesomeIcon icon={s.icon} className={`text-sm ${s.color}`} />
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  {s.sub && <p className="text-[10px] text-slate-400 mt-0.5">{s.sub} of pending</p>}
                </div>
              ))}
            </div>

            {/* Status breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Daily Connects Chart */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Daily Connect Volume</h3>
                <p className="text-xs text-slate-400 mb-4">Sent vs accepted per day (last 30 days)</p>
                <DailyConnectsChart data={connectsData.dailyConnects ?? []} />
              </div>

              {/* Status Breakdown */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Status Breakdown</h3>
                <p className="text-xs text-slate-400 mb-4">Current distribution of all connects</p>
                <div className="space-y-2">
                  {[
                    { key: "accepted", label: "Accepted", color: "#10B981" },
                    { key: "pending", label: "Pending", color: "#F59E0B" },
                    { key: "declined", label: "Declined", color: "#EF4444" },
                    { key: "expired", label: "Expired", color: "#94A3B8" },
                  ].map(s => {
                    const count = connectsData.summary?.[s.key] ?? 0;
                    const total = connectsData.summary?.total || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={s.key} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-20 shrink-0">{s.label}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color }} />
                        </div>
                        <span className="text-xs font-bold text-slate-700 w-14 text-right">{fmt(count)} <span className="font-normal text-slate-400">({pct}%)</span></span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Top Senders + Top Receivers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FontAwesomeIcon icon={faArrowUp} className="text-indigo-400 text-sm" />
                  <h3 className="font-semibold text-slate-800">Top Senders</h3>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left py-1.5 font-medium">Phone</th>
                      <th className="text-left py-1.5 font-medium">Status</th>
                      <th className="text-right py-1.5 font-medium">Connects</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(connectsData.topSenders ?? []).map((s, i) => (
                      <tr key={i}>
                        <td className="py-2 font-medium text-slate-700">{s.phone || "—"}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.status === "ACTIVE" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>
                            {s.status || "—"}
                          </span>
                        </td>
                        <td className="py-2 text-right font-bold text-indigo-600">{s.count}</td>
                      </tr>
                    ))}
                    {!connectsData.topSenders?.length && (
                      <tr><td colSpan={3} className="text-center py-4 text-slate-300">No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FontAwesomeIcon icon={faArrowDown} className="text-pink-400 text-sm" />
                  <h3 className="font-semibold text-slate-800">Top Receivers</h3>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left py-1.5 font-medium">Name</th>
                      <th className="text-left py-1.5 font-medium">Gender</th>
                      <th className="text-right py-1.5 font-medium">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(connectsData.topReceivers ?? []).map((r, i) => (
                      <tr key={i}>
                        <td className="py-2 font-medium text-slate-700">{r.name || r.phone || "—"}</td>
                        <td className="py-2 text-slate-500 capitalize">{r.gender || "—"}</td>
                        <td className="py-2 text-right font-bold text-pink-600">{r.count}</td>
                      </tr>
                    ))}
                    {!connectsData.topReceivers?.length && (
                      <tr><td colSpan={3} className="text-center py-4 text-slate-300">No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default EngagementAnalytics;
